/**
 * PrelaunchSignup — early-access CTA with a progressive modal flow.
 *
 * The public endpoint persists contact details, role, and locale. The
 * conditional step keeps the signup experience relevant without making the
 * landing section feel like a heavy form.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  CheckCircle2,
  Layers,
  Loader2,
  Mail,
  Package,
  Phone as PhoneIcon,
  Truck,
  User,
  UserRound,
  X,
  type LucideIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/Button";
import { Container } from "@/components/ui/Container";
import { cn } from "@/lib/cn";
import type { ApiError } from "@/lib/api";

import {
  lookupCompany,
  submitEarlyAccess,
  type CompanyLookup,
  type EarlyAccessLocale,
  type EarlyAccessRole,
} from "../api";

type Role = EarlyAccessRole;
type SenderType = "private" | "business";
type Step = "intent" | "details" | "contact";

const phoneCountries = [
  { code: "+47", labelKey: "signup.form.phoneCountries.no" },
  { code: "+46", labelKey: "signup.form.phoneCountries.se" },
  { code: "+45", labelKey: "signup.form.phoneCountries.dk" },
  { code: "+358", labelKey: "signup.form.phoneCountries.fi" },
  { code: "+49", labelKey: "signup.form.phoneCountries.de" },
  { code: "+31", labelKey: "signup.form.phoneCountries.nl" },
  { code: "+44", labelKey: "signup.form.phoneCountries.gb" },
  { code: "+1", labelKey: "signup.form.phoneCountries.us" },
] as const;

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { type: "spring" as const, stiffness: 100, damping: 20 } },
};

function isValidEmail(value: string): boolean {
  // Pragmatic email check — sufficient for client-side. Server re-validates.
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function normalizePhone(countryCode: string, phoneNumber: string): string {
  const digits = phoneNumber.replace(/\D/g, "");
  return `${countryCode}${digits}`;
}

function normalizeOrganizationNumber(value: string): string {
  return value.replace(/\D/g, "");
}

function isValidPhone(countryCode: string, phoneNumber: string): boolean {
  const normalized = normalizePhone(countryCode, phoneNumber);
  return /^\+[1-9][0-9]{6,14}$/.test(normalized);
}

export function PrelaunchSignup() {
  const { t, i18n } = useTranslation();

  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [step, setStep] = useState<Step>("intent");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneCountryCode, setPhoneCountryCode] = useState("+47");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [role, setRole] = useState<Role | null>(null);
  const [senderType, setSenderType] = useState<SenderType | null>(null);
  const [companyRegistrationNumber, setCompanyRegistrationNumber] = useState("");
  const [companyLookup, setCompanyLookup] = useState<CompanyLookup | null>(null);
  const [companyLookupLoading, setCompanyLookupLoading] = useState(false);
  const [companyLookupError, setCompanyLookupError] = useState<string | null>(null);
  const [contactSubmitAttempted, setContactSubmitAttempted] = useState(false);
  const [contactErrors, setContactErrors] = useState<{
    name?: string;
    email?: string;
    phone?: string;
  }>({});
  const [errors, setErrors] = useState<{
    role?: string;
    senderType?: string;
    companyRegistrationNumber?: string;
  }>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const roleOptions: { value: Role; labelKey: string; descriptionKey: string; Icon: LucideIcon }[] = [
    {
      value: "shipper",
      labelKey: "signup.form.roleShipper",
      descriptionKey: "signup.form.roleShipperDescription",
      Icon: Package,
    },
    {
      value: "carrier",
      labelKey: "signup.form.roleCarrier",
      descriptionKey: "signup.form.roleCarrierDescription",
      Icon: Truck,
    },
    {
      value: "both",
      labelKey: "signup.form.roleBoth",
      descriptionKey: "signup.form.roleBothDescription",
      Icon: Layers,
    },
  ];

  // Carrier always needs a company lookup. Shipper / both only when they
  // explicitly identify as a business — private individuals skip it.
  const requiresCompanyLookup =
    role === "carrier" ||
    ((role === "shipper" || role === "both") && senderType === "business");

  useEffect(() => {
    const orgnr = normalizeOrganizationNumber(companyRegistrationNumber);
    const shouldLookup = requiresCompanyLookup && orgnr.length === 9;

    setCompanyLookup(null);
    setCompanyLookupError(null);
    if (!shouldLookup) return;

    let cancelled = false;
    const timeoutId = window.setTimeout(() => {
      setCompanyLookupLoading(true);
      void lookupCompany(orgnr)
        .then((company) => {
          if (cancelled) return;
          setCompanyLookup(company);
        })
        .catch((err: ApiError) => {
          if (cancelled) return;
          // Map normalized error codes to translated strings — never show the
          // raw axios "Network Error" on a Norwegian-language UI.
          if (err?.code === "network_error" || err?.status === 0) {
            setCompanyLookupError(t("errors.network"));
          } else if (err?.status === 404) {
            setCompanyLookupError(t("signup.form.companyLookupNotFound"));
          } else if (err?.status === 429 || err?.code === "rate_limited") {
            setCompanyLookupError(t("signup.form.errorRateLimited"));
          } else {
            setCompanyLookupError(t("signup.form.companyLookupError"));
          }
        })
        .finally(() => {
          if (!cancelled) setCompanyLookupLoading(false);
        });
    }, 450);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [companyRegistrationNumber, requiresCompanyLookup, t]);

  useEffect(() => {
    setContactSubmitAttempted(false);
    setContactErrors({});
  }, [step]);

  const openModal = useCallback((initialRole?: Role) => {
    setName("");
    setEmail("");
    setPhoneCountryCode("+47");
    setPhoneNumber("");
    setRole(initialRole ?? null);
    setSenderType(null);
    setCompanyRegistrationNumber("");
    setCompanyLookup(null);
    setCompanyLookupLoading(false);
    setCompanyLookupError(null);
    setSubmitted(false);
    setModalOpen(true);
    // Skip the intent picker when the user already chose a role from the cards.
    setStep(initialRole ? "details" : "intent");
    setErrors({});
    setContactSubmitAttempted(false);
    setContactErrors({});
    setSubmitError(null);
  }, []);

  // Allow other parts of the app (e.g. the header CTA) to trigger the modal
  // without lifting state. Dispatch `vegr:open-early-access` on `window`.
  useEffect(() => {
    const handler = (event: Event) => {
      const role = (event as CustomEvent<{ role?: Role }>).detail?.role;
      openModal(role);
    };
    window.addEventListener("vegr:open-early-access", handler);
    return () => window.removeEventListener("vegr:open-early-access", handler);
  }, [openModal]);

  const closeModal = () => {
    if (submitting) return;
    setModalOpen(false);
    setSubmitError(null);
  };

  // Modal hardening: focus trap, ESC, body scroll lock, autofocus, restore focus.
  useEffect(() => {
    if (!modalOpen) return;

    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
    const dialog = dialogRef.current;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const focusableSelector =
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]';

    // Defer the autofocus so the modal is mounted/painted first.
    const autofocusTimer = window.setTimeout(() => {
      const first = dialog?.querySelector<HTMLElement>(focusableSelector);
      first?.focus();
    }, 0);

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        if (!submitting) {
          setModalOpen(false);
          setSubmitError(null);
        }
        return;
      }
      if (event.key === "Tab" && dialog) {
        const focusables = Array.from(
          dialog.querySelectorAll<HTMLElement>(focusableSelector),
        ).filter((el) => !el.hasAttribute("aria-hidden"));
        if (focusables.length === 0) return;
        const first = focusables[0]!;
        const last = focusables[focusables.length - 1]!;
        const active = document.activeElement as HTMLElement | null;
        if (event.shiftKey && active === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && active === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", onKeyDown);

    return () => {
      window.clearTimeout(autofocusTimer);
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      // Restore focus to whatever opened the modal.
      previouslyFocusedRef.current?.focus?.();
    };
  }, [modalOpen, submitting]);

  const validateIntent = (): boolean => {
    if (role) {
      setErrors((current) => ({ ...current, role: undefined }));
      return true;
    }
    setErrors((current) => ({ ...current, role: t("errors.required") }));
    return false;
  };

  const validateDetails = (): boolean => {
    const next: typeof errors = {};
    if ((role === "shipper" || role === "both") && !senderType) {
      next.senderType = t("errors.required");
    }
    if (requiresCompanyLookup && !companyRegistrationNumber.trim()) {
      next.companyRegistrationNumber = t("errors.required");
    } else if (requiresCompanyLookup && normalizeOrganizationNumber(companyRegistrationNumber).length !== 9) {
      next.companyRegistrationNumber = t("signup.form.companyRegistrationInvalid");
    } else if (requiresCompanyLookup && !companyLookup) {
      next.companyRegistrationNumber = t("signup.form.companyLookupRequired");
    }
    setErrors((current) => ({
      ...current,
      senderType: next.senderType,
      companyRegistrationNumber: next.companyRegistrationNumber,
    }));
    return Object.keys(next).length === 0;
  };

  const validateContact = (): boolean => {
    setContactSubmitAttempted(true);
    const next: typeof contactErrors = {};
    if (!name.trim()) next.name = t("errors.required");
    if (!email.trim()) next.email = t("errors.required");
    else if (!isValidEmail(email)) next.email = t("errors.invalidEmail");
    if (!phoneNumber.trim()) next.phone = t("errors.required");
    else if (!isValidPhone(phoneCountryCode, phoneNumber)) next.phone = t("errors.invalidPhone");
    setContactErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleNext = () => {
    setSubmitError(null);
    if (step === "intent" && validateIntent()) setStep("details");
    if (step === "details" && validateDetails()) {
      setErrors({});
      setContactSubmitAttempted(false);
      setContactErrors({});
      setStep("contact");
    }
  };

  const handleBack = () => {
    setErrors({});
    setContactSubmitAttempted(false);
    setContactErrors({});
    setSubmitError(null);
    if (step === "contact") setStep("details");
    if (step === "details") setStep("intent");
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    if (!role || !validateContact()) return;
    setSubmitting(true);
    try {
      const locale: EarlyAccessLocale = i18n.language?.startsWith("en") ? "en" : "no";
      await submitEarlyAccess({
        name: name.trim(),
        email: email.trim().toLowerCase(),
        phone_number_e164: normalizePhone(phoneCountryCode, phoneNumber),
        role,
        locale,
        organization_number: companyLookup?.organization_number,
        company_name: companyLookup?.name,
      });
      setSubmitted(true);
    } catch (err) {
      const apiErr = err as ApiError;
      if (apiErr?.code === "conflict") {
        setSubmitError(t("signup.form.errorDuplicate"));
      } else if (apiErr?.code === "rate_limited") {
        setSubmitError(t("signup.form.errorRateLimited"));
      } else if (apiErr?.code === "network_error" || apiErr?.status === 0) {
        setSubmitError(t("errors.network"));
      } else {
        setSubmitError(t("signup.form.errorGeneric"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const stepIndex = step === "intent" ? 1 : step === "details" ? 2 : 3;
  const showContactErrors = step === "contact" && contactSubmitAttempted;

  return (
    <section
      className="relative overflow-hidden bg-[#e5ded2] py-24 text-slate-950 lg:py-32"
      id="bli-en-av-de-forste"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_bottom,#d9d1c4,#e5ded2_28%,#e9e2d6)]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,rgba(30,41,59,0.045)_1px,transparent_1px),linear-gradient(to_bottom,rgba(30,41,59,0.045)_1px,transparent_1px)] bg-[size:56px_56px]"
      />

      <Container className="relative max-w-7xl">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeUp}
          className="grid grid-cols-1 gap-8 lg:grid-cols-[0.55fr_0.45fr] lg:items-end"
        >
          <div>
            <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-brand-700">
              {t("signup.label")}
            </span>
            <h2 className="mt-5 max-w-[18ch] text-4xl font-bold leading-[1.02] tracking-tight text-slate-950 md:text-6xl">
              {t("signup.title")}
            </h2>
          </div>
          <p className="max-w-xl text-base leading-relaxed text-slate-600 md:text-lg lg:justify-self-end">
            {t("signup.subtitle")}
          </p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          variants={fadeUp}
          className="mt-12 grid grid-cols-1 gap-5 lg:mt-16 lg:grid-cols-2 lg:gap-6"
        >
          <RoleCard
            Icon={Package}
            kicker={t("signup.tracks.shipper.kicker")}
            title={t("signup.tracks.shipper.title")}
            body={t("signup.tracks.shipper.body")}
            cta={t("signup.tracks.shipper.cta")}
            onClick={() => openModal("shipper")}
          />
          <RoleCard
            Icon={Truck}
            kicker={t("signup.tracks.carrier.kicker")}
            title={t("signup.tracks.carrier.title")}
            body={t("signup.tracks.carrier.body")}
            cta={t("signup.tracks.carrier.cta")}
            onClick={() => openModal("carrier")}
          />
        </motion.div>

        <motion.p
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          variants={fadeUp}
          className="mt-8 text-sm text-slate-600"
        >
          {t("signup.tracks.fallback.prefix")}{" "}
          <button
            type="button"
            onClick={() => openModal()}
            className="font-medium text-brand-700 underline-offset-4 transition-colors hover:text-brand-800 hover:underline focus:outline-none focus-visible:underline"
          >
            {t("signup.tracks.fallback.cta")}
          </button>
        </motion.p>
      </Container>

      {modalOpen && typeof document !== "undefined" && createPortal(
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/80 px-4 py-6 backdrop-blur-md"
          onClick={closeModal}
          role="presentation"
        >
          <motion.div
            ref={dialogRef}
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ type: "spring", stiffness: 140, damping: 22 }}
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="early-access-title"
            className="w-full max-w-xl rounded-3xl border border-slate-800 bg-slate-900 p-6 shadow-2xl md:p-8"
          >
            {submitted ? (
              <div className="flex flex-col items-center gap-4 py-8 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full border border-brand-500/30 bg-brand-500/10">
                  <CheckCircle2 className="h-7 w-7 text-brand-400" strokeWidth={1.5} aria-hidden />
                </span>
                <h3 id="early-access-title" className="text-2xl font-semibold text-slate-50">
                  {t("signup.form.successTitle")}
                </h3>
                <p className="max-w-[40ch] text-sm leading-relaxed text-slate-400">
                  {t("signup.form.successBody")}
                </p>
                <Button type="button" variant="secondary" onClick={closeModal}>
                  {t("common.close")}
                </Button>
              </div>
            ) : (
              <div role="group" aria-labelledby="early-access-title">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div
                      className="flex items-center gap-1.5"
                      role="progressbar"
                      aria-valuemin={1}
                      aria-valuemax={3}
                      aria-valuenow={stepIndex}
                      aria-label={t("signup.form.progress", { step: stepIndex })}
                    >
                      {[1, 2, 3].map((n) => (
                        <span
                          key={n}
                          className={cn(
                            "h-1 rounded-full transition-all duration-300",
                            n < stepIndex && "w-8 bg-brand-500",
                            n === stepIndex && "w-10 bg-brand-400",
                            n > stepIndex && "w-6 bg-slate-700",
                          )}
                          aria-hidden
                        />
                      ))}
                      <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                        {t("signup.form.progress", { step: stepIndex })}
                      </span>
                    </div>
                    <h3
                      id="early-access-title"
                      className="mt-4 text-2xl font-semibold tracking-tight text-slate-50"
                    >
                      {t(`signup.form.${step}.title`)}
                    </h3>
                  </div>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-800 hover:text-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
                    aria-label={t("common.close")}
                  >
                    <X className="h-5 w-5" strokeWidth={1.75} aria-hidden />
                  </button>
                </div>

                <div className="mt-8">
                  <AnimatePresence mode="wait" initial={false}>
                    <motion.div
                      key={step}
                      initial={{ opacity: 0, x: 12 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -12 }}
                      transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
                    >
                  {step === "intent" && (
                    <div className="grid grid-cols-1 gap-3">
                      {roleOptions.map((option) => {
                        const selected = role === option.value;
                        const OptionIcon = option.Icon;
                        return (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => {
                              setRole(option.value);
                              setErrors((current) => ({ ...current, role: undefined }));
                            }}
                            aria-pressed={selected}
                            className={cn(
                              "group flex items-center gap-4 rounded-2xl border p-4 text-left transition-all duration-200",
                              "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900",
                              selected
                                ? "border-brand-500 bg-brand-500/10 text-slate-50 shadow-[inset_0_0_0_1px_rgba(59,130,246,0.4)]"
                                : "border-slate-800 bg-slate-950/40 text-slate-300 hover:border-slate-600 hover:bg-slate-950/60",
                            )}
                          >
                            <span
                              className={cn(
                                "flex h-10 w-10 shrink-0 items-center justify-center rounded-full border transition-colors",
                                selected
                                  ? "border-brand-400/60 bg-brand-500/20 text-brand-200"
                                  : "border-slate-700 bg-slate-900 text-slate-400 group-hover:text-slate-200",
                              )}
                            >
                              <OptionIcon className="h-5 w-5" strokeWidth={1.5} aria-hidden />
                            </span>
                            <span className="flex-1 min-w-0">
                              <span className="block text-sm font-semibold">{t(option.labelKey)}</span>
                              <span className="mt-0.5 block text-xs text-slate-400">
                                {t(option.descriptionKey)}
                              </span>
                            </span>
                            <ArrowRight
                              className={cn(
                                "h-4 w-4 shrink-0 transition-all",
                                selected
                                  ? "text-brand-300 opacity-100"
                                  : "text-slate-600 opacity-0 group-hover:opacity-100",
                              )}
                              strokeWidth={1.75}
                              aria-hidden
                            />
                          </button>
                        );
                      })}
                      {errors.role && (
                        <p className="text-xs text-red-400" role="alert">
                          {errors.role}
                        </p>
                      )}
                    </div>
                  )}

                  {step === "details" && (
                    <div className="flex flex-col gap-5">
                      {(role === "shipper" || role === "both") && (
                        <div className="flex flex-col gap-2">
                          <span className="text-sm font-medium text-slate-200">
                            {t("signup.form.senderTypeLabel")}
                            <span className="text-brand-400" aria-hidden>
                              *
                            </span>
                          </span>
                          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                            {(
                              [
                                { value: "private", Icon: UserRound },
                                { value: "business", Icon: Building2 },
                              ] as const
                            ).map(({ value, Icon }) => {
                              const selected = senderType === value;
                              return (
                                <button
                                  key={value}
                                  type="button"
                                  onClick={() => {
                                    setSenderType(value);
                                    setErrors((current) => ({
                                      ...current,
                                      senderType: undefined,
                                      companyRegistrationNumber: undefined,
                                    }));
                                    if (
                                      (role === "shipper" || role === "both") &&
                                      value === "private"
                                    ) {
                                      setCompanyRegistrationNumber("");
                                      setCompanyLookup(null);
                                      setCompanyLookupError(null);
                                    }
                                  }}
                                  aria-pressed={selected}
                                  className={cn(
                                    "flex items-center gap-3 rounded-xl border px-4 py-3 text-sm transition-all duration-200",
                                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900",
                                    selected
                                      ? "border-brand-500 bg-brand-500/10 text-slate-50 shadow-[inset_0_0_0_1px_rgba(59,130,246,0.4)]"
                                      : "border-slate-800 bg-slate-950/40 text-slate-300 hover:border-slate-600 hover:bg-slate-950/60",
                                  )}
                                >
                                  <Icon
                                    className={cn(
                                      "h-4 w-4 shrink-0 transition-colors",
                                      selected ? "text-brand-300" : "text-slate-500",
                                    )}
                                    strokeWidth={1.5}
                                    aria-hidden
                                  />
                                  <span>{t(`signup.form.senderTypes.${value}`)}</span>
                                </button>
                              );
                            })}
                          </div>
                          {errors.senderType && (
                            <p className="text-xs text-red-400">{errors.senderType}</p>
                          )}
                        </div>
                      )}

                      {requiresCompanyLookup && (
                        <div className="grid grid-cols-1 gap-4">
                          <div className="flex flex-col gap-1.5">
                            <label
                              htmlFor="signup-company-number"
                              className="text-sm font-medium text-slate-200"
                            >
                              {t("signup.form.companyRegistrationLabel")}
                              <span className="text-brand-400" aria-hidden>
                                *
                              </span>
                            </label>
                            <div className="relative">
                              <Building2
                                className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500"
                                strokeWidth={1.5}
                                aria-hidden
                              />
                              <input
                                id="signup-company-number"
                                type="text"
                                inputMode="numeric"
                                value={companyRegistrationNumber}
                                onChange={(e) => {
                                  setCompanyRegistrationNumber(e.target.value);
                                  setErrors((current) => ({
                                    ...current,
                                    companyRegistrationNumber: undefined,
                                  }));
                                }}
                                placeholder={t("signup.form.companyRegistrationPlaceholder")}
                                aria-invalid={Boolean(errors.companyRegistrationNumber)}
                                className={cn(
                                  "h-12 w-full rounded-lg border bg-slate-950/70 pl-10 pr-4 text-sm text-slate-50 placeholder:text-slate-500",
                                  "transition-colors focus:outline-none focus-visible:border-brand-500 focus-visible:ring-2 focus-visible:ring-brand-500/40 focus-visible:ring-offset-0",
                                  errors.companyRegistrationNumber
                                    ? "border-red-500/60"
                                    : "border-slate-700/80 hover:border-slate-600",
                                )}
                              />
                            </div>
                            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                              {t("signup.form.companyRegistrationHelp")}
                            </p>
                            {errors.companyRegistrationNumber && (
                              <span className="text-xs text-red-400" role="alert">
                                {errors.companyRegistrationNumber}
                              </span>
                            )}
                          </div>

                          {(companyLookupLoading || companyLookup || companyLookupError) && (
                            <motion.div
                              initial={{ opacity: 0, y: 8 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="rounded-2xl border border-slate-800 bg-slate-950/50 p-4"
                            >
                              {companyLookupLoading && (
                                <div className="flex items-center gap-3 text-sm text-slate-300">
                                  <Loader2 className="h-4 w-4 animate-spin text-brand-400" aria-hidden />
                                  {t("signup.form.companyLookupLoading")}
                                </div>
                              )}

                              {companyLookupError && !companyLookupLoading && (
                                <p className="text-sm text-red-300">{companyLookupError}</p>
                              )}

                              {companyLookup && !companyLookupLoading && (
                                <div className="space-y-3">
                                  <div>
                                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                      {t("signup.form.companyVerifiedLabel")}
                                    </p>
                                    <p className="mt-1 text-base font-semibold text-slate-50">
                                      {companyLookup.name}
                                    </p>
                                    <p className="text-sm text-slate-400">
                                      {companyLookup.organization_number}
                                      {companyLookup.organization_form_code
                                        ? ` · ${companyLookup.organization_form_code}`
                                        : ""}
                                    </p>
                                  </div>
                                  {(companyLookup.address || companyLookup.postal_place) && (
                                    <p className="text-sm leading-relaxed text-slate-400">
                                      {[companyLookup.address, companyLookup.postal_code, companyLookup.postal_place]
                                        .filter(Boolean)
                                        .join(", ")}
                                    </p>
                                  )}
                                  <div className="flex flex-wrap gap-2">
                                    {companyLookup.vat_registered && (
                                      <span className="rounded-full border border-slate-700 px-2.5 py-1 text-xs text-slate-300">
                                        {t("signup.form.companyVatRegistered")}
                                      </span>
                                    )}
                                    {(companyLookup.bankrupt ||
                                      companyLookup.under_liquidation ||
                                      companyLookup.under_forced_liquidation ||
                                      companyLookup.deleted_date) && (
                                      <span className="rounded-full border border-red-500/40 bg-red-500/10 px-2.5 py-1 text-xs text-red-300">
                                        {t("signup.form.companyHasWarnings")}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              )}
                            </motion.div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {step === "contact" && (
                    <div className="flex flex-col gap-5">
                      <div className="flex flex-col gap-1.5">
                        <label htmlFor="signup-name" className="text-sm font-medium text-slate-200">
                          {t("signup.form.nameLabel")}
                          <span className="text-brand-400" aria-hidden>
                            *
                          </span>
                        </label>
                        <div className="relative">
                          <User
                            className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500"
                            strokeWidth={1.5}
                            aria-hidden
                          />
                          <input
                            id="signup-name"
                            type="text"
                            autoComplete="name"
                            value={name}
                            onChange={(e) => {
                              setName(e.target.value);
                              setContactErrors((current) => ({ ...current, name: undefined }));
                            }}
                            placeholder={t("signup.form.namePlaceholder")}
                            aria-invalid={showContactErrors && Boolean(contactErrors.name)}
                            className={cn(
                              "h-12 w-full rounded-lg border bg-slate-950/70 pl-10 pr-4 text-sm text-slate-50 placeholder:text-slate-500",
                              "transition-colors focus:outline-none focus-visible:border-brand-500 focus-visible:ring-2 focus-visible:ring-brand-500/40",
                              showContactErrors && contactErrors.name
                                ? "border-red-500/60"
                                : "border-slate-700/80 hover:border-slate-600",
                            )}
                          />
                        </div>
                        {showContactErrors && contactErrors.name && (
                          <span className="text-xs text-red-400" role="alert">
                            {contactErrors.name}
                          </span>
                        )}
                      </div>

                      <div className="flex flex-col gap-1.5">
                        <label htmlFor="signup-email" className="text-sm font-medium text-slate-200">
                          {t("signup.form.emailLabel")}
                          <span className="text-brand-400" aria-hidden>
                            *
                          </span>
                        </label>
                        <div className="relative">
                          <Mail
                            className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500"
                            strokeWidth={1.5}
                            aria-hidden
                          />
                          <input
                            id="signup-email"
                            type="email"
                            autoComplete="email"
                            value={email}
                            onChange={(e) => {
                              setEmail(e.target.value);
                              setContactErrors((current) => ({ ...current, email: undefined }));
                            }}
                            placeholder={t("signup.form.emailPlaceholder")}
                            aria-invalid={showContactErrors && Boolean(contactErrors.email)}
                            className={cn(
                              "h-12 w-full rounded-lg border bg-slate-950/70 pl-10 pr-4 text-sm text-slate-50 placeholder:text-slate-500",
                              "transition-colors focus:outline-none focus-visible:border-brand-500 focus-visible:ring-2 focus-visible:ring-brand-500/40",
                              showContactErrors && contactErrors.email
                                ? "border-red-500/60"
                                : "border-slate-700/80 hover:border-slate-600",
                            )}
                          />
                        </div>
                        {showContactErrors && contactErrors.email && (
                          <span className="text-xs text-red-400" role="alert">
                            {contactErrors.email}
                          </span>
                        )}
                      </div>

                      <div className="flex flex-col gap-1.5">
                        <label htmlFor="signup-phone" className="text-sm font-medium text-slate-200">
                          {t("signup.form.phoneLabel")}
                          <span className="text-brand-400" aria-hidden>
                            *
                          </span>
                        </label>
                        <div className="grid grid-cols-[7.5rem_1fr] gap-2">
                          <select
                            value={phoneCountryCode}
                            onChange={(e) => {
                              setPhoneCountryCode(e.target.value);
                              setContactErrors((current) => ({ ...current, phone: undefined }));
                            }}
                            aria-label={t("signup.form.phoneCountryCodeLabel")}
                            className="h-12 rounded-lg border border-slate-700/80 bg-slate-950/70 px-3 text-sm text-slate-50 transition-colors hover:border-slate-600 focus:outline-none focus-visible:border-brand-500 focus-visible:ring-2 focus-visible:ring-brand-500/40"
                          >
                            {phoneCountries.map((country) => (
                              <option key={country.code} value={country.code}>
                                {country.code} {t(country.labelKey)}
                              </option>
                            ))}
                          </select>
                          <div className="relative">
                            <PhoneIcon
                              className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500"
                              strokeWidth={1.5}
                              aria-hidden
                            />
                            <input
                              id="signup-phone"
                              type="tel"
                              autoComplete="tel-national"
                              value={phoneNumber}
                              onChange={(e) => {
                                setPhoneNumber(e.target.value);
                                setContactErrors((current) => ({ ...current, phone: undefined }));
                              }}
                              placeholder={t("signup.form.phonePlaceholder")}
                              aria-invalid={showContactErrors && Boolean(contactErrors.phone)}
                              className={cn(
                                "h-12 w-full rounded-lg border bg-slate-950/70 pl-10 pr-4 text-sm text-slate-50 placeholder:text-slate-500",
                                "transition-colors focus:outline-none focus-visible:border-brand-500 focus-visible:ring-2 focus-visible:ring-brand-500/40",
                                showContactErrors && contactErrors.phone
                                  ? "border-red-500/60"
                                  : "border-slate-700/80 hover:border-slate-600",
                              )}
                            />
                          </div>
                        </div>
                        {showContactErrors && contactErrors.phone && (
                          <span className="text-xs text-red-400">{contactErrors.phone}</span>
                        )}
                      </div>
                    </div>
                  )}
                    </motion.div>
                  </AnimatePresence>
                </div>

                {submitError && (
                  <p
                    role="alert"
                    className="mt-5 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300"
                  >
                    {submitError}
                  </p>
                )}

                <div className="mt-8 flex items-center justify-between gap-3">
                  {step !== "intent" ? (
                    <Button type="button" variant="ghost" onClick={handleBack}>
                      <ArrowLeft className="h-4 w-4" strokeWidth={1.5} aria-hidden />
                      {t("common.back")}
                    </Button>
                  ) : (
                    <span />
                  )}
                  {step === "contact" ? (
                    <Button type="button" loading={submitting} onClick={handleSubmit}>
                      {submitting ? t("signup.form.submitting") : t("signup.form.submit")}
                    </Button>
                  ) : (
                    <Button type="button" onClick={handleNext}>
                      {t("common.next")}
                    </Button>
                  )}
                </div>

                {step === "contact" && (
                  <p className="mt-5 text-xs leading-relaxed text-slate-500">
                    {t("signup.form.consent")}
                  </p>
                )}
              </div>
            )}
          </motion.div>
        </div>,
        document.body,
      )}
    </section>
  );
}

function RoleCard({
  Icon,
  kicker,
  title,
  body,
  cta,
  onClick,
}: {
  Icon: LucideIcon;
  kicker: string;
  title: string;
  body: string;
  cta: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group relative flex flex-col overflow-hidden rounded-2xl border border-[#d2cabb] bg-[#f7f3eb]/85 p-7 text-left shadow-[0_24px_60px_-52px_rgba(15,23,42,0.55)] backdrop-blur-sm transition-colors duration-200 hover:border-brand-600/60 hover:bg-[#f9f4ea] focus:outline-none focus-visible:border-brand-600 focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-[#e5ded2] sm:p-8"
    >
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-full border border-brand-400/40 bg-brand-500/10 text-brand-700 transition-colors group-hover:border-brand-500 group-hover:text-brand-800">
          <Icon className="h-5 w-5" strokeWidth={1.5} aria-hidden />
        </span>
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-700/80">
          {kicker}
        </span>
      </div>
      <h3 className="mt-6 text-2xl font-semibold tracking-tight text-slate-950 md:text-3xl">
        {title}
      </h3>
      <p className="mt-3 max-w-[36ch] text-sm leading-relaxed text-slate-600 md:text-base">
        {body}
      </p>
      <span className="mt-7 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-brand-700 transition-transform duration-200 group-hover:translate-x-0.5">
        {cta}
        <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
      </span>
    </button>
  );
}
